import React, { useCallback, useMemo, useRef } from 'react';
import type { EmbeddedQuickFillActions } from '../../quick-fill';

export interface MappingWorkspaceGuideActions {
  openGuide: () => void;
  openTour?: () => void;
}

export interface MappingWorkspaceFocusRefs {
  mobileMenuButton: React.RefObject<HTMLButtonElement | null>;
  navigation: React.RefObject<HTMLDivElement | null>;
  selectedObjectTypeNav: React.RefObject<HTMLDivElement | null>;
  generalNav: React.RefObject<HTMLDivElement | null>;
  processConditionsNav: React.RefObject<HTMLDivElement | null>;
  projectParametersNav: React.RefObject<HTMLDivElement | null>;
  statusBuilderNav: React.RefObject<HTMLDivElement | null>;
  statusCategoryNav: React.RefObject<HTMLDivElement | null>;
  mappingCanvas: React.RefObject<HTMLDivElement | null>;
  tableControls: React.RefObject<HTMLTableRowElement | null>;
  pcfColumn: React.RefObject<HTMLDivElement | null>;
  revitColumn: React.RefObject<HTMLDivElement | null>;
  typeColumn: React.RefObject<HTMLDivElement | null>;
  formulaColumn: React.RefObject<HTMLDivElement | null>;
  rowActions: React.RefObject<HTMLDivElement | null>;
  generalSection: React.RefObject<HTMLDivElement | null>;
  processConditions: React.RefObject<HTMLDivElement | null>;
  projectParameters: React.RefObject<HTMLDivElement | null>;
  advancedSection: React.RefObject<HTMLDivElement | null>;
  statusBuilderSection: React.RefObject<HTMLDivElement | null>;
  statusCategorySection: React.RefObject<HTMLDivElement | null>;
  footerActions: React.RefObject<HTMLDivElement | null>;
  workspaceCanvas: React.RefObject<HTMLDivElement | null>;
  workspaceApplyAttentionTraceKey: React.MutableRefObject<string | null>;
  quickFillLayoutTraceKey: React.MutableRefObject<string | null>;
  shownMappingRepairToastKeys: React.MutableRefObject<Set<string>>;
  quickFillActions: React.MutableRefObject<EmbeddedQuickFillActions | null>;
  quickFillTabShell: React.RefObject<HTMLDivElement | null>;
  validationGuideActions: React.MutableRefObject<MappingWorkspaceGuideActions | null>;
  exportGuideActions: React.MutableRefObject<MappingWorkspaceGuideActions | null>;
}

export interface UseMappingWorkspaceFocusRefsResult {
  focusRefs: MappingWorkspaceFocusRefs;
  setQuickFillWorkspaceShellRef: (node: HTMLDivElement | null) => void;
}

export function useMappingWorkspaceFocusRefs(): UseMappingWorkspaceFocusRefsResult {
  const shownMappingRepairToastKeysRef = useRef<Set<string>>(new Set());
  const quickFillActionsRef = useRef<EmbeddedQuickFillActions | null>(null);
  const quickFillTabShellRef = useRef<HTMLDivElement | null>(null);
  const validationGuideActionsRef = useRef<MappingWorkspaceGuideActions | null>(null);
  const exportGuideActionsRef = useRef<MappingWorkspaceGuideActions | null>(null);
  const mobileMenuButtonFocusRef = useRef<HTMLButtonElement | null>(null);
  const navigationFocusRef = useRef<HTMLDivElement | null>(null);
  const selectedObjectTypeNavFocusRef = useRef<HTMLDivElement | null>(null);
  const generalNavFocusRef = useRef<HTMLDivElement | null>(null);
  const processConditionsNavFocusRef = useRef<HTMLDivElement | null>(null);
  const projectParametersNavFocusRef = useRef<HTMLDivElement | null>(null);
  const mappingCanvasFocusRef = useRef<HTMLDivElement | null>(null);
  const tableControlsFocusRef = useRef<HTMLTableRowElement | null>(null);
  const pcfColumnFocusRef = useRef<HTMLDivElement | null>(null);
  const revitColumnFocusRef = useRef<HTMLDivElement | null>(null);
  const typeColumnFocusRef = useRef<HTMLDivElement | null>(null);
  const formulaColumnFocusRef = useRef<HTMLDivElement | null>(null);
  const rowActionsFocusRef = useRef<HTMLDivElement | null>(null);
  const generalSectionFocusRef = useRef<HTMLDivElement | null>(null);
  const processConditionsFocusRef = useRef<HTMLDivElement | null>(null);
  const projectParametersFocusRef = useRef<HTMLDivElement | null>(null);
  const advancedSectionFocusRef = useRef<HTMLDivElement | null>(null);
  const statusBuilderNavFocusRef = useRef<HTMLDivElement | null>(null);
  const statusCategoryNavFocusRef = useRef<HTMLDivElement | null>(null);
  const statusBuilderSectionFocusRef = useRef<HTMLDivElement | null>(null);
  const statusCategorySectionFocusRef = useRef<HTMLDivElement | null>(null);
  const workspaceCanvasRef = useRef<HTMLDivElement | null>(null);
  const footerActionsFocusRef = useRef<HTMLDivElement | null>(null);
  const workspaceApplyAttentionTraceKeyRef = useRef<string | null>('');
  const quickFillLayoutTraceKeyRef = useRef<string | null>('');

  const setQuickFillWorkspaceShellRef = useCallback((node: HTMLDivElement | null) => {
    workspaceCanvasRef.current = node;
    quickFillTabShellRef.current = node;
  }, []);

  const focusRefs: MappingWorkspaceFocusRefs = useMemo(
    () => ({
      mobileMenuButton: mobileMenuButtonFocusRef,
      navigation: navigationFocusRef,
      selectedObjectTypeNav: selectedObjectTypeNavFocusRef,
      generalNav: generalNavFocusRef,
      processConditionsNav: processConditionsNavFocusRef,
      projectParametersNav: projectParametersNavFocusRef,
      statusBuilderNav: statusBuilderNavFocusRef,
      statusCategoryNav: statusCategoryNavFocusRef,
      mappingCanvas: mappingCanvasFocusRef,
      tableControls: tableControlsFocusRef,
      pcfColumn: pcfColumnFocusRef,
      revitColumn: revitColumnFocusRef,
      typeColumn: typeColumnFocusRef,
      formulaColumn: formulaColumnFocusRef,
      rowActions: rowActionsFocusRef,
      generalSection: generalSectionFocusRef,
      processConditions: processConditionsFocusRef,
      projectParameters: projectParametersFocusRef,
      advancedSection: advancedSectionFocusRef,
      statusBuilderSection: statusBuilderSectionFocusRef,
      statusCategorySection: statusCategorySectionFocusRef,
      footerActions: footerActionsFocusRef,
      workspaceCanvas: workspaceCanvasRef,
      workspaceApplyAttentionTraceKey: workspaceApplyAttentionTraceKeyRef,
      quickFillLayoutTraceKey: quickFillLayoutTraceKeyRef,
      shownMappingRepairToastKeys: shownMappingRepairToastKeysRef,
      quickFillActions: quickFillActionsRef,
      quickFillTabShell: quickFillTabShellRef,
      validationGuideActions: validationGuideActionsRef,
      exportGuideActions: exportGuideActionsRef,
    }),
    []
  );

  return { focusRefs, setQuickFillWorkspaceShellRef };
}
